import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';

import { useTranslation } from 'react-i18next';

const EXPECTED_COOKIES = {
  necessary: [
    { name: 'opushr_cookie_consent', service: 'OpusHR' },
    { name: 'i18nextLng', service: 'OpusHR' },
  ],
  analytics: [
    { name: '_ga, _gid, _gat', service: 'Google Analytics' },
    { name: '_hjSessionUser, _hjSession', service: 'Hotjar' },
    { name: '_clck, _clsk', service: 'Microsoft Clarity' },
  ],
  marketing: [
    { name: '_fbp, _fbc, fr', service: 'Meta Pixel' },
    { name: '_gcl_au, _gcl_aw', service: 'Google Ads' },
  ],
};

const CookieSettings = ({
  consent,
  updateConsent,
  saveSettings,
  acceptAll,
  closeSettings,
}) => {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState({
    necessary: false,
    analytics: false,
    marketing: false,
  });

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSettings();
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const renderCategory = (key, isDisabled) => {
    const items = EXPECTED_COOKIES[key] || [];

    return (
      <div className='CookieCategory' key={key}>
        <div className='CookieCategory__header'>
          <label className='CookieCategory__label'>
            <input
              type='checkbox'
              className='CookieCategory__checkbox'
              checked={isDisabled ? true : consent[key]}
              disabled={isDisabled}
              readOnly={isDisabled}
              onChange={
                isDisabled
                  ? undefined
                  : (e) => updateConsent(key, e.target.checked)
              }
            />
            <span className='CookieCategory__name'>
              {t(`cookieBanner.categories.${key}.name`)}
            </span>
            {isDisabled && (
              <span className='CookieCategory__badge CookieCategory__badge--required'>
                {t('cookieBanner.required')}
              </span>
            )}
          </label>
          <button
            className='CookieCategory__toggle'
            onClick={() => toggleCategory(key)}
            type='button'
            aria-label='Toggle cookie list'
          >
            {expandedCategories[key] ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>
        <p className='CookieCategory__description'>
          {t(`cookieBanner.categories.${key}.description`)}
        </p>
        {expandedCategories[key] && items.length > 0 && (
          <ul className='CookieCategory__list'>
            {items.map((item) => (
              <li key={item.service} className='CookieCategory__item'>
                <span className='CookieCategory__item-service'>
                  {item.service}
                </span>
                <span className='CookieCategory__item-cookies'>
                  {item.name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className='CookieSettings' onClick={handleBackdropClick}>
      <div className='CookieSettings__modal'>
        <div className='CookieSettings__header'>
          <h2 className='CookieSettings__title'>
            {t('cookieBanner.settingsTitle')}
          </h2>
          <button
            className='CookieSettings__close'
            onClick={closeSettings}
            aria-label={t('common.close')}
          >
            <FiX />
          </button>
        </div>

        <div className='CookieSettings__content'>
          <div className='CookieSettings__categories'>
            {renderCategory('necessary', true)}
            {renderCategory('analytics', false)}
            {renderCategory('marketing', false)}
          </div>
        </div>

        <div className='CookieSettings__actions'>
          <button
            className='CookieSettings__button CookieSettings__button--save'
            onClick={saveSettings}
          >
            {t('cookieBanner.saveSettings')}
          </button>
          <button
            className='CookieSettings__button CookieSettings__button--accept'
            onClick={acceptAll}
          >
            {t('cookieBanner.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

CookieSettings.propTypes = {
  consent: PropTypes.shape({
    necessary: PropTypes.bool,
    analytics: PropTypes.bool,
    marketing: PropTypes.bool,
  }).isRequired,
  updateConsent: PropTypes.func.isRequired,
  saveSettings: PropTypes.func.isRequired,
  acceptAll: PropTypes.func.isRequired,
  closeSettings: PropTypes.func.isRequired,
};

export default CookieSettings;
